/**
 * Configuration Sync types — shared between backend and admin console.
 *
 * Design: docs/FeatureDesigns/ConfigSync.md (if created)
 *
 * Supports export/import of all configuration entities across environments
 * (dev/prod). All entities share stable IDs across environments, so matching
 * is by document ID.
 */

// ── Export bundle ───────────────────────────────────────────────────────

export interface ConfigBundle {
    version: 1;
    exportedAt: string;            // ISO timestamp
    exportedBy: string;            // admin userId
    exportedByName?: string;       // admin name (resolved at export time for cross-env readability)
    environment: string;           // source environment label (e.g., 'dev', 'prod')
    sections: ConfigBundleSections;
}

/**
 * Each section holds documents in their original Cosmos shape, with
 * `_rid`, `_self`, `_etag`, `_attachments` stripped. `_ts` is kept for
 * last-modified display in the diff UI.
 */
export interface ConfigBundleSections {
    prompts?: Record<string, unknown>[];
    providers?: Record<string, unknown>;
    providerTieredSets?: Record<string, unknown>;
    providerParallelSets?: Record<string, unknown>;
    pipelineConfigs?: Record<string, unknown>[];
    appSettings?: Record<string, unknown>;
    whatsappSettings?: Record<string, unknown>;
    /**
     * Notification rules — the "how" of a notification (recipients, per-channel
     * templates, dedupe keys). Portable: those mean the same thing everywhere.
     *
     * Notification ROLLOUT is deliberately not a section. Ring membership is a
     * statement about one environment's users, so importing dev's rollout into
     * prod would re-stage a rollout someone already ran. See
     * docs/FeatureDesigns/notification-registry-and-rollout.md decision 7.
     */
    notificationRules?: Record<string, unknown>[];
}

export type ConfigSectionName = keyof ConfigBundleSections;

export const ALL_SECTION_NAMES: ConfigSectionName[] = [
    'prompts',
    'providers',
    'providerTieredSets',
    'providerParallelSets',
    'pipelineConfigs',
    'appSettings',
    'whatsappSettings',
    'notificationRules',
];

// ── Import dry-run result ───────────────────────────────────────────────

export interface ConfigDiffItem {
    key: string;               // document id
    label: string;             // human-readable name
    currentValue?: unknown;    // what target currently has
    incomingValue?: unknown;   // what bundle contains
    diffSummary?: string;      // short text description of change
}

export interface ConfigDiffResult {
    adds: ConfigDiffItem[];
    updates: ConfigDiffItem[];
    unchanged: ConfigDiffItem[];
    deletes: ConfigDiffItem[];       // in target but not in bundle (informational)
}

export interface ConfigImportDryRunResult {
    sections: Partial<Record<ConfigSectionName, ConfigDiffResult>>;
}

// ── Import apply request/result ─────────────────────────────────────────

export interface ConfigImportRequest {
    bundle: ConfigBundle;
    selections: Partial<Record<ConfigSectionName, { keys: string[] }>>;
    importedBy: string;
}

export interface ConfigImportApplyItemResult {
    key: string;
    label: string;
}

export interface ConfigImportApplySkippedResult {
    key: string;
    label: string;
    reason: string;
}

export interface ConfigApplyResult {
    created: ConfigImportApplyItemResult[];
    updated: ConfigImportApplyItemResult[];
    skipped: ConfigImportApplySkippedResult[];
}

export interface ConfigImportApplyResult {
    sections: Partial<Record<ConfigSectionName, ConfigApplyResult>>;
}
